import React, { useEffect, useState } from 'react';

interface NavItem {
    id: string;
    label: string;
}

const NAV_ITEMS: NavItem[] = [
    { id: 'account-details', label: 'Account Details' },
    { id: 'change-password', label: 'Password' },
    { id: 'note-encryption', label: 'New note encryption' },
    { id: 'plan-usage',      label: 'Plan & Usage' },
    { id: 'remove-account',  label: 'Remove Account' },
];

const AccountNav: React.FC = () => {
    const [activeId, setActiveId] = useState<string>(() => {
        const hash = window.location.hash.slice(1);
        return NAV_ITEMS.some((i) => i.id === hash) ? hash : NAV_ITEMS[0].id;
    });

    // Scroll to hash on initial load
    useEffect(() => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            const el = document.getElementById(hash);
            if (el) {
                // Small delay so the page layout is settled before scrolling
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
            }
        }
    }, []);

    // Track active section while scrolling and keep hash in sync
    useEffect(() => {
        const observers: IntersectionObserver[] = [];

        NAV_ITEMS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (!el) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setActiveId(id);
                        window.history.replaceState(null, '', `#${id}`);
                    }
                },
                { rootMargin: '-30% 0px -60% 0px', threshold: 0 },
            );

            observer.observe(el);
            observers.push(observer);
        });

        return () => observers.forEach((o) => o.disconnect());
    }, []);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            window.history.replaceState(null, '', `#${id}`);
        }
    };

    return (
        <nav aria-label="Account sections">
            <ul className="space-y-1">
                {NAV_ITEMS.map(({ id, label }) => {
                    const isActive = activeId === id;
                    return (
                        <li key={id}>
                            <a
                                href={`#${id}`}
                                onClick={(e) => handleClick(e, id)}
                                className={[
                                    'block px-3 py-2 rounded-md text-sm font-medium transition-colors',
                                    isActive
                                        ? 'bg-primary/10 text-primary'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100',
                                ].join(' ')}
                            >
                                {label}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default AccountNav;
